import { SubmitStatus, SubmitStatuses } from '@/util/storeUtilities';
import axios from 'axios';
import { createUseStore } from 'wired-react';
import { createStore } from 'wired-store';
import { API_URL } from '~/modules/config';
import { selectData, serializeAxiosError } from '~/modules/utilities/axios_utils';
import { handleException } from '~/modules/utilities/cross_env_utils';
import { globalStores } from '@/util/globalStores';

export interface SubscribePartnerStoreState {
  submitStatus: SubmitStatus;
  notifySuccess: string;
  notifyFailure: string;
  payload: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
    partnershipType: string;
    message: string;
  };
}

const initialState: SubscribePartnerStoreState = {
  submitStatus: SubmitStatuses.UNINITIALIZED,
  notifySuccess: '',
  notifyFailure: '',
  payload: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    partnershipType: '',
    message: '',
  },
};

export const subscribePartnerStore = createStore(
  initialState,
  (set, get) => ({
    setFirstName(value: string) {
      set(function setFirstName(draft) {
        draft.payload.firstName = value;
      });
    },
    setLastName(value: string) {
      set(function setLastName(draft) {
        draft.payload.lastName = value;
      });
    },
    setEmail(value: string) {
      set(function setEmail(draft) {
        draft.payload.email = value;
      });
    },
    setPhone(value: string) {
      set(function setPhone(draft) {
        draft.payload.phone = value;
      });
    },
    setCompany(value: string) {
      set(function setCompany(draft) {
        draft.payload.company = value;
      });
    },
    setPartnershipType(value: string) {
      set(function setPartnershipType(draft) {
        draft.payload.partnershipType = value;
      });
    },
    setMessage(value: string) {
      set(function setMessage(draft) {
        draft.payload.message = value;
      });
    },
    clearNotifySuccess() {
      set(function clearNotifySuccess(draft) {
        draft.notifySuccess = '';
      });
    },
    clearNotifyFailure() {
      set(function clearNotifyFailure(draft) {
        draft.notifyFailure = '';
      });
    },
    async submit() {
      try {
        // Setting the submit status here allows us to disable the submit button while it's processing
        set(function beginSubmit(draft) {
          draft.submitStatus = SubmitStatuses.SUBMITTING;
        });

        const payload = get().payload;
        const response = await axios
          .post(`${API_URL}/subscribe/partner`, JSON.stringify(payload), {
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then(selectData);

        if (!response.ok) {
          throw new Error('Failed to submit subscription');
        }

        // You could just call subscribeStore.reset() here, which would do the same thing,
        // but I think this makes the code more explicit, and helps you see how async actions work in stores.
        // PS: Async actions usually have multiple set() calls, combined with one or more awaits.
        set(function completeSubmit(draft) {
          draft.payload = initialState.payload; // Reset the payload for a new subscription
          draft.submitStatus = initialState.submitStatus; // Reset the status (re-enable the submit button)
          draft.notifySuccess = 'Thank you for joining our waitlist!';
        });
      } catch (error) {
        handleException(error);
        set(function failedSubmit(draft) {
          draft.submitStatus = SubmitStatuses.FAILED;
          draft.notifyFailure = serializeAxiosError(error).message;
        });
      }
    },
  }),
  {
    storeName: 'subscribePartnerStore',
    attachTo: globalStores(),
    warnAgainstAnonymousActions: IS_DEVELOPMENT,
  },
);

// This hook is used to connect react components to the store
export const useSubscribePartnerStore = createUseStore(subscribePartnerStore);

export function selectSubcribePartnerStatus(state: SubscribePartnerStoreState) {
  return state.submitStatus;
}

export function selectStorePayload(state: SubscribePartnerStoreState) {
  return state.payload;
}
